import type { UUID } from "../../../utils/uuid";
import type { UnlayerDesign } from "../types/unlayer-design";

export interface DisplayConditionCallbackContext {
  item: {
    type: string;
    id: string | number;
  };
  column?: {
    id: string | number;
  };
  row?: {
    id: string | number;
  };
}

interface ConditionConfig {
  type?: string;
  label?: string;
  description?: string;
  before?: string;
  after?: string;
}

interface ConditionValues {
  displayCondition?: ConditionConfig | null;
}

const hasDisplayCondition = (value: unknown): boolean => {
  if (!value || typeof value !== "object") return false;

  const condition = value as ConditionConfig;
  return Boolean(
    condition.type ||
    condition.label ||
    condition.description ||
    condition.before ||
    condition.after,
  );
};

const getAllRows = (design: UnlayerDesign | null | undefined): UnlayerDesign["body"]["rows"] => {
  if (!design) return [];

  return [...design.body.rows, ...(design.body.headers ?? []), ...(design.body.footers ?? [])];
};

const findRowForContext = (
  design: UnlayerDesign | null | undefined,
  context: DisplayConditionCallbackContext,
): UnlayerDesign["body"]["rows"][number] | undefined => {
  const rows = getAllRows(design);

  if (context.row?.id) return rows.find((row) => row.id === context.row?.id);

  return rows.find(
    (row) =>
      row.id === context.item.id ||
      row.columns.some(
        (column) =>
          column.id === context.item.id ||
          column.contents.some((content) => content.id === context.item.id),
      ),
  );
};

const findColumnForContext = (
  row: UnlayerDesign["body"]["rows"][number] | undefined,
  context: DisplayConditionCallbackContext,
): UnlayerDesign["body"]["rows"][number]["columns"][number] | undefined => {
  if (!row) return;

  if (context.column?.id) return row.columns.find((column) => column.id === context.column?.id);

  return row.columns.find(
    (column) =>
      column.id === context.item.id ||
      column.contents.some((content) => content.id === context.item.id),
  );
};

export const hasNestedDisplayConditionAncestor = (
  design: UnlayerDesign | null | undefined,
  context: DisplayConditionCallbackContext,
): boolean => {
  const row = findRowForContext(design, context);
  if (!row || row.id === context.item.id) return false;

  const rowHasCondition = hasDisplayCondition((row.values as ConditionValues).displayCondition);
  const column = findColumnForContext(row, context);

  if (column?.id === context.item.id) return rowHasCondition;

  const columnHasCondition = hasDisplayCondition(
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    (column?.values as ConditionValues | undefined)?.displayCondition,
  );
  return rowHasCondition || columnHasCondition;
};

export const getDisplayConditionDialogState = (
  design: UnlayerDesign | null | undefined,
  request: Partial<{ id: string | number }> & { context: DisplayConditionCallbackContext },
): { nestedBlocked: boolean; selectedId?: UUID } => ({
  nestedBlocked: hasNestedDisplayConditionAncestor(design, request.context),
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  selectedId: typeof request.id === "string" ? (request.id as UUID) : undefined,
});
